import React, { useEffect, useMemo, useState } from 'react';
import {
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Divider,
    useMediaQuery,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import TabPanel from '@mui/lab/TabPanel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import Loading from '../components/common/Loading';
import AppSnackbar from '../common/AppSnackbar';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReservationAdminAPI } from '../service/useReservationAdminAPI';
import { parseISO } from 'date-fns';
import ReservationTile from '../components/management/ReservationTile';
import AppointmentTile from '../components/management/AppointmentTile';
import {useTenantConfig} from "../security/tenant-config-context";
import AddReservation from "../components/management/AddReservation";
import AddAppointment from "../components/management/AddAppointment";

const personModel = {
    bus: '',
    country: '',
    email: '',
    forename: '',
    gsm: '',
    municipality: '',
    name: '',
    number: '',
    postalCode: '', // sometimes API returns postalcode (lowercase)
    postalcode: '',
    street: '',
    animals: [],
    emergencyContact: {},
    veterinary: {},
    reservations: [],
};

const InfoRow = ({ label, content, stack = false }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: stack ? 'column' : 'row',
            flexWrap: stack ? 'nowrap' : 'wrap',
            columnGap: stack ? 0 : 1,
            rowGap: stack ? 0.25 : 0.5,
            alignItems: stack ? 'flex-start' : 'baseline',
        }}
    >
        <Typography fontWeight={600} variant={stack ? 'caption' : undefined} sx={{ opacity: stack ? 0.8 : 1 }}>
            {label}
        </Typography>
        <Typography component="span" sx={{ mt: stack ? 0.25 : 0 }}>
            {content ? (stack ? content : `: ${content}`) : stack ? '-' : ': -'}
        </Typography>
    </Box>
);

const addressStr = (p) =>
    `${p?.street ?? ''} ${p?.number ?? ''}${p?.box ? ' ' + p.box : ''}, ${p?.postalCode ?? p?.postalcode ?? ''} ${
        p?.municipality ?? ''
    }`.trim();

const AnimalInfo = ({ animal, t, isXs }) => (
    <Box sx={{ display: 'grid', gap: 1, mb: 1.5 }}>
        <InfoRow label={t('name')} content={animal?.name} stack={isXs} />
        <InfoRow label={t('birthDate')} content={animal?.birthDate} stack={isXs} />
        <InfoRow label={t('breed')} content={animal?.breed} stack={isXs} />
    </Box>
);

const AnimalDetail = () => {
    const { t } = useTranslation();
    const { getCustomerDetail } = useReservationAdminAPI();
    const { uuid } = useParams();

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));

    const [currentPerson, setCurrentPerson] = useState(personModel);
    const [value, setValue] = useState('1');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const [editReservationData, setEditReservationData] = useState(null);
    const [editAppointmentData, setEditAppointmentData] = useState(null);

    const {cfg} = useTenantConfig();
    const isHotelFlow = cfg?.businessTypes?.includes('HOTEL_BASED');

    function handleFetchError(reason) {
        setSnackbarMessage(reason);
        setOpenSnackbar(true);
    }

    useEffect(() => {
        setIsLoading(true);
        getCustomerDetail(uuid)
            .then((val) => {
                if (val != null) setCurrentPerson(val);
                else throw new Error('Geen persoon gevonden');
            })
            .catch(() => handleFetchError(t('fetchPersonError')))
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    const refreshData = () => {
        getCustomerDetail(uuid)
            .then((val) => {
                if (val != null) setCurrentPerson(val);
            })
            .catch(() => handleFetchError(t('fetchPersonError')));
    };

    const handleEditReservation = (reservation: any) => {
        setEditReservationData({
            ...reservation,
            person: currentPerson,
            animals: currentPerson.animals || [],
            animalsInReservationIds: reservation.animalsInReservationIds || (reservation.animalId ? [reservation.animalId] : [])
        });
    };

    const handleEditAppointment = (appointment: any) => {
        setEditAppointmentData({
            ...appointment,
            person: currentPerson,
            personId: currentPerson.id,
            animals: currentPerson.animals || [],
            animalsInReservationIds: appointment.animalsInReservationIds || (appointment.animalId ? [appointment.animalId] : [])
        });
    };

    const handleTabChange = (_e, newValue) => setValue(newValue);

    const now = useMemo(() => new Date(), []);
    const upcomingReservation = (currentPerson?.reservations ?? [])
        .filter((r) => parseISO(r.toDate) > now)
        .map((r, idx) => (
            <ReservationTile 
                key={`up-${idx}`} 
                reservation={r} 
                onEdit={handleEditReservation} 
                t={t} 
            />
        ));

    const upcomingAppointments = (currentPerson?.appointments ?? [])
        .filter((r: any) => parseISO(r.startAt) > now)
        .map((r: any, idx: number) => (
            <AppointmentTile 
                key={`up-app-${idx}`} 
                appointment={r} 
                onEdit={handleEditAppointment} 
                t={t} 
            />
        ));

    const pastReservation = (currentPerson?.reservations ?? [])
        .filter((r) => parseISO(r.toDate) < now)
        .map((r, idx) => (
            <ReservationTile 
                key={`past-${idx}`} 
                reservation={r} 
                t={t} 
            />
        ));

    const pastAppointments = (currentPerson?.appointments ?? [])
        .filter((r: any) => parseISO(r.startAt) < now)
        .map((r: any, idx: number) => (
            <AppointmentTile 
                key={`past-app-${idx}`} 
                appointment={r} 
                t={t} 
            />
        ));

    // Build sections (used by Tabs and Accordions)
    const sections = [
        {
            key: '1',
            label: t('Dieren'),
            node: (
                <Box>
                    {(currentPerson?.animals ?? []).length ? (
                        currentPerson.animals.map((animal, i) => <AnimalInfo key={animal?.uuid || i} animal={animal} t={t} isXs={isXs} />)
                    ) : (
                        <Typography align="left" color="text.secondary">
                            {t('Geen dieren')}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            key: '2',
            label: t('NoodContact'),
            node: (
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            <InfoRow
                                label={t('name')}
                                content={`${currentPerson?.emergencyContact?.forename ?? ''} ${
                                    currentPerson?.emergencyContact?.name ?? ''
                                }`}
                                stack={isXs}
                            />
                            <InfoRow label={t('street')} content={addressStr(currentPerson?.emergencyContact)} stack={isXs} />
                            <InfoRow label={t('country')} content={currentPerson?.emergencyContact?.country} stack={isXs} />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'grid', gap: 1 }}>
                            <InfoRow label={t('gsm')} content={currentPerson?.emergencyContact?.gsm} stack={isXs} />
                            <InfoRow label={t('email')} content={currentPerson?.emergencyContact?.email} stack={isXs} />
                        </Box>
                    </Grid>
                </Grid>
            ),
        },
        {
            key: '3',
            label: t('Dierenarts'),
            node: (
                <Box sx={{ display: 'grid', gap: 1 }}>
                    <InfoRow label={t('practice')} content={currentPerson?.veterinary?.practice} stack={isXs} />
                    <InfoRow label={t('location')} content={currentPerson?.veterinary?.place} stack={isXs} />
                    <InfoRow label={t('customersPage.phone')} content={currentPerson?.veterinary?.phoneNumber} stack={isXs} />
                </Box>
            ),
        },
        ...(isHotelFlow ? [{
            key: '4',
            label: t('reservationsSection'),
            node: (
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" align="left" mb={1.5}>
                            {t('upcomingReservations')}
                        </Typography>
                        {upcomingReservation?.length ? (
                            upcomingReservation
                        ) : (
                            <Typography align="left">{t('noUpcomingReservations')}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" align="left" mb={1.5}>
                            {t('pastReservations')}
                        </Typography>
                        {pastReservation?.length ? (
                            pastReservation
                        ) : (
                            <Typography align="left">{t('noPastReservations')}</Typography>
                        )}
                    </Grid>
                </Grid>
            ),
        }] : []),
        ...(!isHotelFlow
            ? [{
                key: '6',
                label: t('appointmentsSection'),
                node: (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="h6" align="left" mb={1.5}>
                                {t('upcomingAppointments')}
                            </Typography>
                            {upcomingAppointments.length !== 0 ? (
                                upcomingAppointments
                            ) : (
                                <Typography align="left">{t('noUpcomingAppointments')}</Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="h6" align="left" mb={1.5}>
                                {t('pastAppointments')}
                            </Typography>
                            {pastAppointments.length !== 0 ? (
                                pastAppointments
                            ) : (
                                <Typography align="left">{t('noPastAppointments')}</Typography>
                            )}
                        </Grid>
                    </Grid>
                ),
            }] : [])
    ];

    return (
        <>
            {isLoading && <Loading />}
            {!isLoading && (
                <Grid container justifyContent="center" spacing={1} sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
                    {editReservationData && (
                        <AddReservation
                            initialData={editReservationData}
                            reservationId={editReservationData.uuid}
                            onSave={() => {
                                setEditReservationData(null);
                                refreshData();
                            }}
                            onClose={() => setEditReservationData(null)}
                        />
                    )}
                    {editAppointmentData && (
                        <AddAppointment
                            initialData={editAppointmentData}
                            appointmentId={editAppointmentData.uuid}
                            onSave={() => {
                                setEditAppointmentData(null);
                                refreshData();
                            }}
                            onClose={() => setEditAppointmentData(null)}
                        />
                    )}
                    {/* Top summary */}
                    <Grid item xs={12} sm={11} md={10} lg={8}>
                        <Card variant="outlined" sx={{ mb: 1 }}>
                            <CardHeader
                                title={
                                    <Typography color="secondary" variant={isXs ? 'h6' : 'h5'}>
                                        {t('Persoons gegevens')}
                                    </Typography>
                                }
                            />
                            <Divider />
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'grid', gap: 1 }}>
                                            <InfoRow label={t('name')} content={`${currentPerson?.forename ?? ''} ${currentPerson?.name ?? ''}`} stack={isXs} />
                                            <InfoRow label={t('street')} content={addressStr(currentPerson)} stack={isXs} />
                                            <InfoRow label={t('country')} content={currentPerson?.country} stack={isXs} />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'grid', gap: 1 }}>
                                            <InfoRow label={t('gsm')} content={currentPerson?.gsm} stack={isXs} />
                                            <InfoRow label={t('email')} content={currentPerson?.email} stack={isXs} />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Details: Accordions on mobile, Tabs on >= sm */}
                    <Grid item xs={12} sm={11} md={10} lg={8}>
                        {isXs ? (
                            <Card variant="outlined" sx={{ mb: 1 }}>
                                {sections.map((s, i) => (
                                    <Accordion key={s.key} defaultExpanded={i === 0} disableGutters>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1">{s.label}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>{s.node}</AccordionDetails>
                                    </Accordion>
                                ))}
                            </Card>
                        ) : (
                            <Card variant="outlined" sx={{ mb: 1 }}>
                                <TabContext value={value}>
                                    <Box
                                        sx={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                            bgcolor: 'background.paper',
                                            px: 1,
                                            pt: 1,
                                        }}
                                    >
                                        <TabList
                                            onChange={handleTabChange}
                                            variant="scrollable"
                                            scrollButtons
                                            allowScrollButtonsMobile
                                            aria-label="Person detail tabs"
                                            data-testid="customer-detail-tabs"
                                        >
                                            {sections.map((s) => (
                                                <Tab key={s.key} label={s.label} value={s.key} data-testid={`tab-${s.key}`} />
                                            ))}
                                        </TabList>
                                    </Box>
                                    <Divider />
                                    {sections.map((s) => (
                                        <TabPanel key={s.key} value={s.key} sx={{ px: { xs: 1, sm: 2 } }}>
                                            {s.node}
                                        </TabPanel>
                                    ))}
                                </TabContext>
                            </Card>
                        )}
                    </Grid>

                    <AppSnackbar
                        openSnackbar={openSnackbar}
                        snackbarMessage={snackbarMessage}
                        setOpenSnackbar={setOpenSnackbar}
                    />
                </Grid>
            )}
        </>
    );
};

export default withAuthenticationRequired(AnimalDetail, {
    onRedirecting: () => <Loading />,
    returnTo: '/beheer/login',
});
